import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, apiUpload, useAuth } from '@shop/api-client'
import type { Branch, Draft, Warehouse } from '../api/types'
import { useDictation } from '../intake/useDictation'
import { ImageField } from '../ui/ImageField'
import { Banner, Button, ErrorBanner, Field, Panel, Select, TextArea, TextInput } from '@shop/ui'

/**
 * ① فتحُ المسوّدة: إملاءٌ أو ماسحٌ أو كتابة — <b>ولا صنفَ يُولد هنا</b>.
 *
 * هذا ليس ترتيبَ شاشات: قيدٌ في القاعدة يرفض مسوّدةً غيرَ معتمَدةٍ تحمل
 * `variant_id`. فما يقع هنا نصٌّ يُحفظ ويُحلَّل، لا أكثر — والصنفُ يُولد في
 * الاعتماد وحدَه.
 */
export default function IntakeNew() {
  const navigate = useNavigate()
  const { session } = useAuth()

  const [text, setText] = useState('')
  const [barcode, setBarcode] = useState('')

  // ‏`null` = لم يختر الموظّفُ بعد، فيُستنتج. وسلسلةٌ فارغةٌ اختيارٌ صريحٌ بـ«لا
  // شيء». ولولا التفريقُ لَما أمكن للموظّف أن يُفرغ حقلًا مُلئ له.
  const [chosenBranch, setChosenBranch] = useState<string | null>(null)
  const [warehouseId, setWarehouseId] = useState('')
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  // الصورةُ تُختار هنا وتُرفع **بعد** أن تُنشأ المسوّدة: الرفعُ يحتاج مُعرِّفًا،
  // ولا مُعرِّفَ قبل الإنشاء. فتُحفظ في الحالة حتى تلك اللحظة.
  const [image, setImage] = useState<File | null>(null)

  // ‏`source` يُسجَّل في المسوّدة ويبقى — فهو جوابُ «من أين جاء هذا النصّ؟».
  // ولهذا يُشتقّ ممّا جرى فعلًا لا ممّا يبدو: نصٌّ مُملى ثمّ صُحّح بيدٍ يبقى
  // إملاءً، لأنّ قياسَ دقّة المحلّل يُبنى على مسوّدات الإملاء وحدَها.
  const [usedVoice, setUsedVoice] = useState(false)

  // والمُملى يُضاف إلى ما كُتب ولا يمحوه: الموظّفُ يُملي جملةً ثمّ يصحّح كلمةً
  // بيده ثمّ يُملي جملةً ثانية، وهذا هو الاستعمالُ الفعليّ.
  const dictation = useDictation((heard) => {
    setUsedVoice(true)
    setText((current) => (current ? `${current.trimEnd()} ${heard}` : heard))
  })

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => api<Branch[]>('/api/v1/branches'),
  })

  // الفرعُ الحاليُّ يُختار وحدَه — <b>ويُشتقّ في الرسم لا بأثرٍ يضبط حالة</b>:
  // الموظّفُ يعمل في فرعٍ واحدٍ في الغالب، وقائمةٌ فارغةٌ تنتظر اختيارًا تُبطئ
  // مئةَ إدخالٍ في اليوم.
  const branchId =
    chosenBranch ?? session?.me.activeBranchId ?? branches.data?.[0]?.id ?? ''

  const warehouses = useQuery({
    queryKey: ['warehouses', branchId],
    queryFn: () => api<Warehouse[]>(`/api/v1/warehouses?branchId=${branchId}`),
    enabled: branchId !== '',
  })

  const activeWarehouses = (warehouses.data ?? []).filter((w) => w.isActive)

  const source = usedVoice
    ? 'VOICE'
    : text.trim() === '' && barcode.trim() !== ''
      ? 'BARCODE'
      : 'MANUAL'

  const create = async () => {
    setBusy(true)
    setError(null)

    try {
      const draft = await api<Draft>('/api/v1/intake/drafts', {
        method: 'POST',
        body: {
          source,
          branchId,
          rawText: text.trim() === '' ? null : text.trim(),

          // ⚠ الباركودُ الممسوحُ يُرسل في حقله لا في النصّ: رقمٌ في النصّ
          // يمرّ بحارسَي الطول ورقم التحقّق ويُهمَل إن سقط، والممسوحُ مؤكَّدٌ
          // ولا يُفحص فحصَ المنطوق.
          barcode: barcode.trim() === '' ? null : barcode.trim(),
          warehouseId: warehouseId === '' ? null : warehouseId,
        },
      })

      // ⚠ ورفعُ الصورة لا يُسقط المسوّدة إن سقط: المسوّدةُ حُفظت ومعها النصّ،
      //   وإسقاطُ كلّ شيءٍ لأنّ صورةً لم تصعد يُضيع عملَ الموظّف. تُقال الرسالةُ
      //   في شاشة المراجعة حيث تُستأنف الصورةُ بضغطة.
      if (image !== null) {
        try {
          await apiUpload(`/api/v1/intake/drafts/${draft.id}/image`, image)
        } catch {
          // صامتٌ هنا عمدًا — ويُرى هناك: المسوّدةُ تُفتح بلا صورة.
        }
      }

      navigate(`/drafts/${draft.id}`)
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  const empty = text.trim() === '' && barcode.trim() === ''

  return (
    <>
      <Panel title="إدخالُ مادّةٍ جديدة">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="الفرع">
            <Select value={branchId} onChange={setChosenBranch} disabled={busy}>
              <option value="">اختر الفرع…</option>
              {(branches.data ?? []).map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.nameAr}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="المستودع"
            hint="اتركه فارغًا إن كنت تُنشئ الصنفَ بلا كميّة — والكميّةُ تحتاج مستودعًا مخوَّلًا."
          >
            <Select value={warehouseId} onChange={setWarehouseId} disabled={busy || branchId === ''}>
              <option value="">بلا مستودع (صنفٌ فقط)</option>
              {activeWarehouses.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.nameAr}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </Panel>

      <Panel
        title="الإملاء"
        actions={
          dictation.supported ? (
            <Button
              tone={dictation.listening ? 'danger' : 'primary'}
              onClick={dictation.listening ? dictation.stop : dictation.start}
              disabled={busy}
            >
              {dictation.listening ? '■ إيقاف' : '● ابدأ الإملاء'}
            </Button>
          ) : null
        }
      >
        {!dictation.supported && (
          <div className="mb-3">
            <Banner tone="info">
              هذا المتصفّحُ لا يدعم الإملاء (كروم وإدج يدعمانه). اكتب النصَّ في الحقل
              أدناه — ويُحلَّل بالطريقة نفسِها تمامًا.
            </Banner>
          </div>
        )}

        {dictation.listening && (
          <div className="mb-3">
            <Banner tone="ok">
              يستمع… قل مثلًا: «كفر آيفون ١٥ أسود، الكمية عشرة، الكلفة دينار ونص، سعر البيع ثلاثة».
              {dictation.interim && <span className="block opacity-60">{dictation.interim}</span>}
            </Banner>
          </div>
        )}

        {dictation.error && (
          <div className="mb-3">
            <Banner tone="warn">{dictation.error}</Banner>
          </div>
        )}

        <Field
          label="النصّ كما سيصل الخادم"
          hint="الخادمُ هو من يحلّله. صحّح ما سمعه الجهازُ خطأً قبل الإرسال."
        >
          <TextArea value={text} onChange={setText} rows={5} disabled={busy} />
        </Field>
      </Panel>

      <Panel title="الصورة">
        <ImageField
          path={null}
          busy={busy}
          onPick={setImage}
          onRemove={() => setImage(null)}
          hint="تُرفع مع المسوّدة، وتنتقل إلى الصنف عند الاعتماد. صوّر العلبةَ أو الملصق."
        />
      </Panel>

      <Panel title="الماسح">
        <Field
          label="الباركود"
          hint="اسحب الماسحَ وهذا الحقلُ مُركَّز — أو اكتب الرقمَ يدويًّا."
        >
          <TextInput
            value={barcode}
            onChange={setBarcode}
            dir="ltr"
            disabled={busy}
            placeholder="190198001733"
            onKeyDown={(event) => {
              // الماسحُ لوحةُ مفاتيحَ في نظر المتصفّح، ويُنهي بـEnter. ولولا
              // منعُ الافتراضيّ لَأرسل النموذجَ قبل أن يُختار مستودع.
              if (event.key === 'Enter') {
                event.preventDefault()
              }
            }}
          />
        </Field>
      </Panel>

      <ErrorBanner error={error} />

      <div className="flex flex-wrap items-center gap-3">
        <Button tone="primary" disabled={busy || empty || branchId === ''} onClick={() => void create()}>
          {busy ? 'يُحفظ…' : 'حفظ المسوّدة ومراجعتُها'}
        </Button>

        {empty && (
          <span className="text-xs text-ink-soft">
            أملِ أو اكتب أو امسح باركودًا — المسوّدةُ الفارغةُ لا شيءَ فيها يُحلَّل.
          </span>
        )}
      </div>
    </>
  )
}
